import React from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { toggleSnackBar, toggleSpinner, toggleModal, toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DetailsItem } from 'apps/auth/components/common/DetailsItem/DetailsItem';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { IconWrapper } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import IbanAccountDetailsModal from 'apps/auth/components/ga/accounts/modals/IbanAccountDetailsModal';
import OnfidoWaitingStatus from 'apps/auth/components/ga/kyc/KYCOnfidoStatus/OnfidoWaitingStatus';
import { GET_ACCOUNT_DETAILS } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { logInfo } from 'apps/auth/helpers/clientLogger';
import { getIbanWithWhitespace } from 'apps/auth/helpers/historyUtils';
import { copyTextToClipboard } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { requestForReceivingIban, showPopupForCorrectAddress } from 'apps/auth/services/GaAccountsService';
import media from 'apps/auth/theme/media';
const defaultAccount = {
    id: null,
    accountDetails: {
        name: '',
        sortCode: '',
        number: '',
        shareDetails: '',
    },
    iban: null,
    bic: null,
};

export const DetailsItemsRow = styled.div`
    ${media.tablet720} {
        display: flex;
    }
`;
export const DetailsItemsContainer = styled.div`
    ${media.tablet720} {
        width: 50%;
    }
`;

const KYC_LEVEL_TWO = 2;

const GaPocketAccountDetails = (props) => {
    const {
        linkKyc,
        account = defaultAccount,
        isSepa,
        showAccountDetails,
        userFullName,
        sharedDetails,
        currentLevel,
        isWaitingStatus,
        IsBritishAccountInGbp,
        hideEurAccountDetails,
    } = useExtra();
    const messages = usePageMessages();
    const { popupRef } = props;

    const renderField = (label, value) => {
        const copyOnClick = (e) => {
            e.preventDefault();
            const result = copyTextToClipboard(value.replaceAll(' ', ''));
            if (result) {
                props.toggleSnackBar(true, `${label} ${messages.copied}`);
            }
        };

        return (
            <Row indent="adaptive-32-48">
                <DetailsItem detailsTitle={label} detailsText={value} detailsButton={messages.detailsCopy} detailsButtonClick={copyOnClick} />
            </Row>
        );
    };

    const renderShareDetails = (title, text) => {
        const share = (e) => {
            e.preventDefault();
            navigator
                .share({ title, text })
                .then(() => amplitude.getInstance().logEvent(GET_ACCOUNT_DETAILS))
                .catch((error) => logInfo('Error sharing', error));
        };

        return (
            <>
                {__isBrowser__ && navigator && navigator.share && (
                    <Button type="button" variant="row" caption={messages.detailsShare} onClick={share} negativeMargin />
                )}
            </>
        );
    };

    const renderDescription = (text) => (
        <Text variant="div" indent="adaptive-32-48">
            {text}
        </Text>
    );

    const showStatus = (e) => {
        if (isWaitingStatus && currentLevel < KYC_LEVEL_TWO) {
            e.preventDefault();
            props.toggleModal(true, <OnfidoWaitingStatus />);
        }
    };

    const requestIban = (e) => {
        e.preventDefault();
        requestForReceivingIban(
            account.id,
            {
                toggleSpinner: props.toggleSpinner,
                toggleModal: props.toggleModal,
                toggleModalWithTimer: props.toggleModalWithTimer,
                updatePageMessages: props.updatePageMessages,
                addExtras: props.addExtras,
                popupRef,
            },
            <IbanAccountDetailsModal onClickNotNow={() => props.toggleModal(false)} />,
            false,
            messages.accountDetailsIssued,
        );
    };

    const getAddressViaChat = (e) => {
        e.preventDefault();
        showPopupForCorrectAddress(popupRef, props.toggleChat, messages);
    };

    const renderGetDetails = (link, onClickHandlerGetDetails) => {
        return (
            <Panel type="reactLink" onClick={onClickHandlerGetDetails} to={link} hasIcon isHoverable negativeMargin>
                <IconWrapper>
                    <Icon name="document" color="primary" />
                </IconWrapper>
                <Text color="primary">{messages.getAccountDetails}</Text>
            </Panel>
        );
    };

    const renderTitle = () => (
        <Text variant="div" size="subheader" indent="adaptive-16-24" color="secondary">
            {messages.accountDetails}
        </Text>
    );

    if (!hideEurAccountDetails && isSepa && showAccountDetails) {
        return (
            <>
                {renderTitle()}
                {renderDescription(messages.accountDetailsDescription)}
                {renderField(messages.detailsName, userFullName)}
                {renderField(messages.ibanLabel, getIbanWithWhitespace(account.iban))}
                {renderField(messages.bicLabel, account.bic)}
                {renderShareDetails(messages.accountDetails, sharedDetails)}
                <Separator />
            </>
        );
    } else if (!hideEurAccountDetails && isSepa && currentLevel < KYC_LEVEL_TWO) {
        return (
            <>
                {renderTitle()}
                {renderDescription(messages.sepaIsNotAvailable)}
                {renderGetDetails(linkKyc ? linkKyc : '#', showStatus)}
                <Separator />
            </>
        );
    } else if (!hideEurAccountDetails && isSepa && currentLevel >= KYC_LEVEL_TWO) {
        return (
            <>
                {renderTitle()}
                {renderDescription(messages.accountReceivedIbanNotification)}
                {renderGetDetails('#', requestIban)}
                <Separator />
            </>
        );
    } else if (IsBritishAccountInGbp && !account.accountDetails?.number) {
        return (
            <>
                {renderTitle()}
                {renderDescription(messages.receiveAccountText)}
                {renderGetDetails('#', getAddressViaChat)}
                <Separator />
            </>
        );
    } else if (account.accountDetails) {
        return (
            <>
                {renderTitle()}
                {renderDescription(messages.accountNotification)}
                {renderField(messages.detailsName, account.accountDetails.name)}
                <DetailsItemsRow>
                    <DetailsItemsContainer>{renderField(messages.detailsCode, account.accountDetails.sortCode)}</DetailsItemsContainer>
                    <DetailsItemsContainer>{renderField(messages.detailsNumber, account.accountDetails.number)}</DetailsItemsContainer>
                </DetailsItemsRow>
                {renderShareDetails(messages.detailsShareTitle, account.accountDetails.sharedDetails)}
                <Separator />
            </>
        );
    }

    return null;
};
export default connect(null, { toggleSnackBar, toggleSpinner, toggleModal, toggleModalWithTimer, updatePageMessages, addExtras, toggleChat })(
    GaPocketAccountDetails,
);
